import PropTypes from 'prop-types';
import { useEffect, useMemo, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
// hooks
import { useAuth } from 'src/hooks/useAuth';
// components
import { SplashScreen } from 'src/components/loading-screen';
// utils
import axiosInstance, { endpoints } from 'src/utils/axios';
// store
import { setRights } from 'src/store/slices/authSlice';

// ----------------------------------------------------------------------

/**
 * RightsGuard - Protects routes based on user rights
 * Checks if user has required module/menu/submenu access with specific action
 */
export function RightsGuard({
    children,
    module,
    menu,
    submenu,
    feature,
    action,
    requiredRole,
    excludeRoles,
    skipRightsCheck = false,
    featureFlag // New prop: name of the feature flag in organizationFeatures
}) {
    const dispatch = useDispatch();
    const { user, rights: globalRights } = useAuth();
    const [hasRight, setHasRight] = useState(null);
    const [isInPlan, setIsInPlan] = useState(true);
    const [isFeatureEnabled, setIsFeatureEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [fetchComplete, setFetchComplete] = useState(false);
    
    // Prevent multiple fetches in the same mount
    const fetchRef = useRef(null);

    // Standardize feature/action prop
    const targetFeature = useMemo(() => feature || action || 'view_all', [feature, action]);

    // Resolve user role
    const userRole = useMemo(() => user?.adminRef?.role || user?.role, [user]);

    // Check excluded roles
    const isRoleExcluded = useMemo(() => {
        if (!Array.isArray(excludeRoles) || excludeRoles.length === 0) return false;
        return excludeRoles.includes(userRole);
    }, [excludeRoles, userRole]);

    // Check required role
    const isRoleAuthorized = useMemo(() => {
        if (!requiredRole) return true;
        if (userRole === 'super_admin') return true;
        return userRole === requiredRole;
    }, [requiredRole, userRole]);

    // 1. Fetching Effect
    useEffect(() => {
        const fetchInitialRights = async () => {
            const userId = user?.id || user?._id;
            
            if (!userId || userRole === 'super_admin' || userRole === 'admin') {
                setFetchComplete(true);
                return;
            }

            const currentKey = `${userId}-${module || ''}-${menu || ''}-${submenu || ''}`;
            if (fetchRef.current === currentKey) return;
            fetchRef.current = currentKey;

            try {
                const response = await axiosInstance.get(endpoints.specificUserRights.get(userId));
                const userRights = response.data.data.rights;
                if (userRights) {
                    dispatch(setRights(userRights));
                }
            } catch (error) {
                console.error('Failed to fetch rights in Guard:', error);
            } finally {
                setFetchComplete(true);
            }
        };

        fetchInitialRights();
    }, [user?.id, user?._id, userRole, module, menu, submenu, dispatch]);

    // 2. Evaluation Effect
    useEffect(() => {
        const check = () => {
            if (!fetchComplete) return;

            try {
                // Priority 1: Role-based exclusion
                if (isRoleExcluded || !isRoleAuthorized) {
                    setHasRight(false);
                    return;
                }

                if (!user) {
                    setHasRight(false);
                    return;
                }

                // Priority 1.5: Organization Feature Flag check
                if (featureFlag && user?.organizationFeatures) {
                    if (user.organizationFeatures[featureFlag] === false && userRole !== 'super_admin') {
                        setIsFeatureEnabled(false);
                        return;
                    }
                }

                // Priority 2: Plan-based whitelist (CRITICAL: affects everyone including super_admin if inside org dashboard)
                const planWhitelist = user?.planWhitelist;
                if (planWhitelist) {
                    if (module && planWhitelist.modules && !planWhitelist.modules.includes(module)) {
                        setIsInPlan(false);
                        return;
                    }
                    if (menu && menu !== 'forget_punch' && planWhitelist.menus && !planWhitelist.menus.includes(menu)) {
                        setIsInPlan(false);
                        return;
                    }
                    if (submenu && submenu !== 'forget_punch' && submenu !== 'mark' && submenu !== 'apply' && planWhitelist.submenus && !planWhitelist.submenus.includes(submenu)) {
                        setIsInPlan(false);
                        return;
                    }
                }

                // Priority 3: Bypass for Super Admin / Admin (if in plan)
                if (userRole === 'super_admin') {
                    setHasRight(true);
                    return;
                }

                if (userRole === 'admin') {
                    setHasRight(module !== 'super_admin');
                    return;
                }

                // Priority 4: Skip rights check for 'Common' menus
                if (skipRightsCheck) {
                    setHasRight(true);
                    return;
                }

                // Priority 5: Specific User Rights check (Moderator)
                if (!module || !menu) {
                    setHasRight(true);
                    return;
                }

                const rights = globalRights || {};
                const moduleItem = rights[module];
                
                if (!moduleItem) {
                    setHasRight(false);
                    return;
                }

                const menuItem = moduleItem.children?.[menu];
                if (!menuItem) {
                    setHasRight(false);
                    return;
                }

                let target = menuItem;
                if (submenu) {
                    target = menuItem.children?.[submenu] || {};
                }

                const actions = target || {};

                if (targetFeature === 'view_all') {
                    setHasRight(!!(actions.view_all || actions.view_self || actions.view));
                } else {
                    setHasRight(!!actions[targetFeature]);
                }

            } finally {
                setLoading(false);
            }
        };

        check();
    }, [user, userRole, isRoleExcluded, isRoleAuthorized, globalRights, module, menu, submenu, targetFeature, fetchComplete, skipRightsCheck, featureFlag]);

    if (loading) {
        return <SplashScreen />;
    }

    if (!isInPlan || !isFeatureEnabled) {
        return <Navigate to="/403" replace />;
    }

    if (hasRight === false) {
        return <Navigate to="/403" replace />;
    }

    return <>{children}</>;
}

RightsGuard.propTypes = {
    children: PropTypes.node,
    module: PropTypes.string,
    menu: PropTypes.string,
    submenu: PropTypes.string,
    feature: PropTypes.string,
    action: PropTypes.string,
    requiredRole: PropTypes.string,
    excludeRoles: PropTypes.arrayOf(PropTypes.string),
    skipRightsCheck: PropTypes.bool,
    featureFlag: PropTypes.string,
};
